import { useState } from 'react';
import { useT } from '@/store';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState, SkeletonRows, EmptyState } from '@/components/ui/Panel';
import { GPS_REPORT_PERIODS, useGpsFleetReport } from '@/hooks/useGpsReport';
import { groupDigits } from '@/lib/format';

/**
 * Fleet-wide rollup of core/gps_report.py's aggregate_fleet(): which
 * quarries — own and competitor — get visited most, by how many distinct
 * trucks, across the whole fleet. This is the competitive-intelligence
 * question the competitor-quarry KMZ import was originally for, answered
 * at fleet scope instead of opening every truck's report and adding it up
 * by hand — see VehicleGpsReportModal.jsx for the per-vehicle version this
 * complements. Opened from the Transportas toolbar (VehicleGrid.jsx).
 */
export function GpsFleetReportModal({ open, onClose }) {
  const t = useT();
  const [days, setDays] = useState(30);
  const { data, isLoading, isError, error } = useGpsFleetReport(days);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('gpsReport.fleetTitle')}
      width="max-w-[820px]"
      footer={
        <button type="button" className="btn" onClick={onClose}>
          {t('common.closeBtn')}
        </button>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-1.5">
          {GPS_REPORT_PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              className={`btn ${days === period ? 'btn-primary' : ''}`}
              onClick={() => setDays(period)}
            >
              {t(`gpsReport.period${period}`)}
            </button>
          ))}
        </div>

        {isError ? <ErrorState error={error} /> : null}
        {isLoading ? <SkeletonRows rows={6} /> : null}

        {!isLoading && data ? (
          data.vehicles_with_data === 0 ? (
            <EmptyState>{t('gpsReport.fleetNoData')}</EmptyState>
          ) : (
            <>
              <p className="text-[11px] text-dim">
                {t('gpsReport.fleetCoverage', { withData: data.vehicles_with_data, total: data.vehicles_total })}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat
                  label={t('gpsReport.ownQuarry')}
                  value={fmtMin(data.fleet_own_quarry_minutes)}
                  sub={t('gpsReport.visitsN', { n: data.fleet_own_quarry_visits })}
                  tone="text-green"
                />
                <Stat
                  label={t('gpsReport.competitorQuarry')}
                  value={fmtMin(data.fleet_competitor_quarry_minutes)}
                  sub={t('gpsReport.visitsN', { n: data.fleet_competitor_quarry_visits })}
                  tone="text-amber"
                />
                <Stat label={t('gpsReport.quarriesVisited')} value={data.quarries.length} />
                {data.estimated_leakage_eur != null ? (
                  <Stat
                    label={t('gpsReport.estimatedLeakage')}
                    value={fmtEur(data.estimated_leakage_eur)}
                    tone="text-amber"
                    title={t('gpsReport.estimatedLeakageHint')}
                  />
                ) : null}
              </div>
              <p className="text-[10.5px] text-dim">{t('gpsReport.estimateNote')}</p>
              {data.estimated_leakage_eur != null ? (
                <p className="text-[10.5px] text-amber">{t('gpsReport.estimatedLeakageHint')}</p>
              ) : null}

              <QuarriesTable quarries={data.quarries} t={t} />
            </>
          )
        ) : null}
      </div>
    </Modal>
  );
}

function fmtMin(minutes) {
  if (minutes >= 60) return `${(minutes / 60).toFixed(1)} val`;
  return `${Math.round(minutes)} min`;
}

/** Same rounding convention as VehicleGpsReportModal.jsx's fmtEur() — kept
 *  duplicated rather than shared, same as fmtMin() above already is between
 *  these two modals. The GROUPING is shared (V-352): it is the product's
 *  own convention rather than this modal's, and having it written out per
 *  modal is what let both reach for `toLocaleString('en-US')` and print a
 *  comma where Lithuanian puts its decimal separator. */
function fmtEur(eur) {
  const rounded = eur >= 100 ? Math.round(eur / 10) * 10 : Math.round(eur);
  return `≈€${groupDigits(rounded)}`;
}

function QuarriesTable({ quarries, t }) {
  if (quarries.length === 0) return null;
  return (
    <div>
      <h4 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
        {t('gpsReport.fleetQuarriesHeading')}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[11px]">
          <thead>
            <tr className="text-dim">
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.quarry')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.type')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.vehicles')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.visits')}</th>
              <th className="px-2 py-1 font-semibold">{t('gpsReport.col.dwell')}</th>
            </tr>
          </thead>
          <tbody>
            {quarries.map((q) => (
              <tr key={q.quarry_id} className="border-t border-line">
                <td className="px-2 py-1 font-semibold text-ink">{q.quarry_name}</td>
                <td className={`px-2 py-1 font-semibold ${q.quarry_type === 'own' ? 'text-green' : 'text-amber'}`}>
                  {t(`gpsReport.class.${q.quarry_type}_quarry`)}
                </td>
                <td className="px-2 py-1">{q.vehicle_count}</td>
                <td className="px-2 py-1">{q.visits}</td>
                <td className="px-2 py-1">{fmtMin(q.total_dwell_minutes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone = 'text-ink', title }) {
  return (
    <div className="rounded-[10px] border border-line bg-white/2 px-3 py-2" title={title}>
      <div className={`text-[16px] font-bold ${tone}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.03em] text-dim">{label}</div>
      {sub ? <div className="text-[10px] text-dim">{sub}</div> : null}
    </div>
  );
}
